import { useEffect, useState } from "react";
import { Camera, CheckCircle2, FileSignature, LifeBuoy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDateTime, fmtDay } from "@/lib/format";
import { Btn, Chip, Pill } from "@/components/rp";
import { DocItem, Section } from "@/components/commesse";
import { SignaturePad } from "@/components/SignaturePad";
import { CertList, mediaUrl } from "@/components/admin/fineLavori";

export const useClientChiusura = (oid) => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/client/orders/${oid}/chiusura`).then((r) => setC(r.data)).catch(() => {}); }, [oid]);
  return [c, setC];
};

export const VerbalePreview = ({ c, oid }) => (
  <div className="space-y-4 mb-5" data-testid="verbale-preview">
    <div>
      <p className="rp-label mb-1">Lavori eseguiti</p>
      <p className="text-sm whitespace-pre-line bg-white rounded-2xl p-4 border border-[#EBEBEB]">{c.verbale.lavori || "—"}</p>
    </div>
    <div>
      <p className="rp-label mb-1">Punti da sistemare</p>
      {!c.difetti.length && <p className="text-sm">Nessun difetto rilevato.</p>}
      {c.difetti.map((x) => (
        <div key={x.id} className="flex items-center gap-3 text-sm bg-white rounded-2xl p-3 border border-[#EBEBEB] mb-2" data-testid={`client-difetto-${x.id}`}>
          {x.photos[0] && <img src={mediaUrl(oid, x.photos[0])} alt="" className="h-12 w-12 rounded-lg object-cover" />}
          <span className="flex-1">{x.description}{x.scadenza && <span className="block text-xs text-[#757575]">Entro il {fmtDay(x.scadenza)}</span>}</span>
          <Pill tone={x.status === "risolto" ? "success" : "warning"}>{x.status_label}</Pill>
        </div>
      ))}
    </div>
    {c.verbale.photo_refs?.length > 0 && (
      <div className="grid grid-cols-4 gap-2">{c.verbale.photo_refs.map((r) => <img key={r.pid} src={fileUrl(`/api/diario/${r.eid}/photos/${r.pid}`)} alt="" className="aspect-square w-full rounded-xl object-cover" />)}</div>
    )}
    {c.verbale.note && <p className="text-sm text-[#757575]">Note: {c.verbale.note}</p>}
  </div>
);

const AssistenzaForm = ({ oid, c, setC }) => {
  const [text, setText] = useState("");
  const [g, setG] = useState(c.garanzie[0]?.id || "");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const fd = new FormData();
    fd.append("description", text); fd.append("garanzia_id", g);
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try { setC((await api.post(`/client/orders/${oid}/assistenza`, fd)).data); setText(""); setFiles([]); toast.success("Richiesta inviata: ti ricontattiamo al più presto"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3 mt-4" data-testid="assistenza-form">
      <select value={g} onChange={(e) => setG(e.target.value)} className="rp-field bg-white" data-testid="assistenza-garanzia">{c.garanzie.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}<option value="">Non so / altro</option></select>
      <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} className="rp-field bg-white" placeholder="Descrivi il problema (dove, da quando…)" data-testid="assistenza-text" />
      <div className="flex items-center gap-3">
        <label className="rp-btn bg-white border border-[#EBEBEB] cursor-pointer h-10" data-testid="assistenza-photos"><Camera className="h-4 w-4" /> Foto ({files.length})<input type="file" multiple accept="image/*" className="hidden" onChange={(e) => setFiles([...files, ...e.target.files].slice(0, 6))} data-testid="assistenza-photos-input" /></label>
        <Btn className="ml-auto" loading={busy} disabled={text.trim().length < 5} onClick={send} data-testid="assistenza-send"><LifeBuoy className="h-4 w-4" /> Richiedi assistenza</Btn>
      </div>
    </div>
  );
};

const RitocchiSign = ({ c, oid, setC }) => {
  const [r, setR] = useState({ ok: true, note: "", name: "", signature: null });
  const [busy, setBusy] = useState(false);
  const dif = c.difetti.filter((x) => c.ritocchi.difetti.includes(x.id));
  const sign = async () => { setBusy(true); try { setC((await api.post(`/client/orders/${oid}/ritocchi/sign`, r)).data); toast.success("Verbale ritocchi firmato, grazie!"); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  return (
    <Section label="Firma il verbale ritocchi" className="bg-[#E6DDCE]/40 border-[#C8BDAB]" testid="client-ritocchi-box">
      <div className="space-y-2 mb-4">{dif.map((x) => (
        <div key={x.id} className="rounded-2xl bg-white border border-[#EBEBEB] p-3 text-sm" data-testid={`client-ritocco-${x.id}`}>
          <p className="font-medium">{x.description}</p>{x.resolution_note && <p className="text-xs text-[#757575]">{x.resolution_note}</p>}
          <div className="flex gap-2 mt-2">{x.photos.slice(0, 1).map((p) => <figure key={p.id}><img src={mediaUrl(oid, p)} alt="prima" className="h-20 w-20 rounded-lg object-cover" /><figcaption className="text-[10px] text-[#757575]">Prima</figcaption></figure>)}
            {x.photos_after.map((p) => <figure key={p.id}><img src={mediaUrl(oid, p)} alt="dopo" className="h-20 w-20 rounded-lg object-cover ring-2 ring-[#198754]" /><figcaption className="text-[10px] text-[#198754]">Dopo</figcaption></figure>)}</div>
        </div>
      ))}</div>
      <div className="flex gap-2"><Chip active={r.ok} onClick={() => setR({ ...r, ok: true })} data-testid="ritocchi-ok">Tutto sistemato</Chip><Chip active={!r.ok} onClick={() => setR({ ...r, ok: false })} data-testid="ritocchi-ko">Resta qualcosa</Chip></div>
      <textarea rows={2} value={r.note} onChange={(e) => setR({ ...r, note: e.target.value })} className="rp-field mt-3 bg-white" placeholder={r.ok ? "Un commento? (facoltativo)" : "Cosa non va ancora?"} data-testid="ritocchi-note" />
      <input value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} className="rp-field mt-3 bg-white" placeholder="Nome e cognome (firma)" data-testid="ritocchi-name" />
      <div className="mt-3"><SignaturePad onChange={(signature) => setR((x) => ({ ...x, signature }))} testid="ritocchi-signature" /></div>
      <Btn className="mt-4" loading={busy} disabled={r.name.trim().length < 3 || !r.signature || (!r.ok && r.note.trim().length < 3)} onClick={sign} data-testid="ritocchi-sign-button"><FileSignature className="h-4 w-4" /> Firma il verbale ritocchi</Btn>
    </Section>
  );
};

export const ClientPostCantiere = ({ oid }) => {
  const [c, setC] = useClientChiusura(oid);
  if (!c) return null;
  return (
    <>
      {c.ritocchi?.sent_at && !c.ritocchi.signed && <RitocchiSign c={c} oid={oid} setC={setC} />}
      {c.verbali.length > 0 && (
        <Section label="I tuoi verbali da scaricare" testid="client-verbali">
          <div className="space-y-2">{c.verbali.map((d) => <DocItem key={d.id} d={d} href={`/api/orders/${oid}/documents/${d.id}/file`}><CheckCircle2 className="h-4 w-4 text-[#198754]" /></DocItem>)}</div>
        </Section>
      )}
      {c.certificati.length > 0 && <Section label="Certificati degli impianti" testid="client-certificati"><CertList oid={oid} list={c.certificati} /></Section>}
      {c.signed && (
        <Section label="Garanzie e assistenza" testid="client-garanzie">
          <div className="space-y-2">
            {c.garanzie.map((g) => (
              <div key={g.id} className="flex items-center gap-3 rounded-2xl bg-white border border-[#EBEBEB] p-3 text-sm" data-testid={`client-garanzia-${g.id}`}>
                <ShieldCheck className={`h-5 w-5 ${g.active ? "text-[#198754]" : "text-[#757575]"}`} />
                <span className="flex-1">{g.label}<span className="block text-xs text-[#757575]">Dal {fmtDay(g.start)} al {fmtDay(g.end)}</span></span>
                <Pill tone={g.active ? "success" : "muted"}>{g.active ? "Attiva" : "Scaduta"}</Pill>
              </div>
            ))}
          </div>
          {c.assistenza.map((i) => <p key={i.id} className="text-xs text-[#757575] mt-3">Richiesta del {fmtDateTime(i.created_at)}: «{i.description}» · {i.status === "risolta" ? "risolta" : "in gestione"}</p>)}
          <AssistenzaForm oid={oid} c={c} setC={setC} />
        </Section>
      )}
    </>
  );
};
